import { ButtonHTMLAttributes, forwardRef, ReactNode } from "react";
import { cx } from "../../utils/cx";
import styles from "./Button.module.scss";

/**
 * Visual hierarchy of the action.
 * - `primary`: filled brand-green button. Highest emphasis.
 * - `secondary`: bordered green text on transparent. Medium emphasis.
 * - `tertiary`: green text only, no border. Lowest emphasis.
 *
 * Names match the Figma `Button v2 (beta)` component variants 1:1.
 */
export type ButtonVariant = "primary" | "secondary" | "tertiary";

/**
 * Control height + typography preset.
 * - `sm`: 32px / text 12 / radius 12
 * - `md`: 40px / text 15 / radius 16  (default)
 * - `lg`: 52px / text 15 / radius 16
 *
 * Sizes match the three explicit options offered in Figma; xs/xl are not
 * design-supported. If a future need arises, add them in lockstep with Figma.
 */
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    /** Visual hierarchy. Defaults to `primary`. */
    variant?: ButtonVariant;
    /** Control height + type preset. Defaults to `md`. */
    size?: ButtonSize;
    /** Optional icon node rendered before the label. */
    iconLeft?: ReactNode;
    /** Optional icon node rendered after the label. */
    iconRight?: ReactNode;
    /** Disables interactions and shows a spinner. */
    loading?: boolean;
    /** Stretches to 100% width of its container. */
    fullWidth?: boolean;
    /** Renders an icon-only button (square, no label padding). */
    iconOnly?: boolean;
}

/**
 * Treez DS — Button (Wave 1).
 *
 * Design source: Figma `Button v2 (beta) 🆕` — file `qgFv3OQga6RQ4luvSFuGFP`.
 * Tokens: `@/theme/tokens.scss`.
 *
 * State coverage (per Figma):
 * - enabled / hovered / pressed / focused / disabled
 *
 * Focus ring: 2.5px solid black (`--ds-color-black`) using `outline` so the
 * layout doesn't shift. Triggered with `:focus-visible` (keyboard only).
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    function Button(
        {
            variant = "primary",
            size = "md",
            iconLeft,
            iconRight,
            loading = false,
            fullWidth = false,
            iconOnly = false,
            className,
            children,
            disabled,
            type = "button",
            ...rest
        },
        ref,
    ) {
        const isDisabled = disabled || loading;

        return (
            <button
                aria-busy={loading || undefined}
                aria-disabled={isDisabled || undefined}
                className={cx(
                    styles.root,
                    fullWidth && styles.fullWidth,
                    iconOnly && styles.iconOnly,
                    className,
                )}
                data-loading={loading || undefined}
                data-size={size}
                data-ui="button"
                data-variant={variant}
                disabled={isDisabled}
                ref={ref}
                type={type}
                {...rest}
            >
                {loading && <span aria-hidden className={styles.spinner} />}
                {iconLeft && !loading && (
                    <span aria-hidden className={styles.icon}>
                        {iconLeft}
                    </span>
                )}
                {!iconOnly && children !== undefined && (
                    <span className={styles.label}>{children}</span>
                )}
                {iconOnly && children}
                {iconRight && !loading && (
                    <span aria-hidden className={styles.icon}>
                        {iconRight}
                    </span>
                )}
            </button>
        );
    },
);

Button.displayName = "Button";
